import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

/**
 * The app chrome's cutoff: below `md` (960px) the layout is the phone layout —
 * MobileHeader, bottom tabs, one column, panels as overlays.
 *
 * A wrapper rather than a hand-written `useMediaQuery(theme.breakpoints.down(…))`
 * per component, because those copies disagreed with each other: the chrome asked
 * `md` and the data tables asked `sm`, so a component that asked the wrong one
 * rendered its desktop treatment inside the mobile shell. That mismatch is most of
 * why the 600–960px band looks unfinished.
 */
export const useIsMobile = (): boolean => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.down('md'))
}

/**
 * The narrower cutoff (below `sm`, 600px), for surfaces that genuinely need to
 * know "this is a phone-width viewport" rather than "the chrome is mobile".
 *
 * Kept distinct from `useIsMobile` on purpose — they are two different questions,
 * and collapsing them would silently change behaviour for every table and chart
 * in the 600–960px band.
 */
export const useIsNarrow = (): boolean => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.down('sm'))
}

/**
 * Touch-first input, independent of viewport width.
 *
 * This is the right question for hit targets, hover affordances and gestures: a
 * 1280px iPad has no hover and needs the touch treatment, while a narrow desktop
 * window has a mouse and does not. Width-based checks get both of those wrong.
 *
 * Subscribe once per surface and pass the result down rather than calling this in
 * a list row — `useMediaQuery` registers its own listener per calling component,
 * and a timeline of a thousand rows each holding one is not free.
 */
export const useIsTouch = (): boolean => useMediaQuery('(pointer: coarse)')

export default useIsMobile
